// src/server/j.ts
import { cors } from "hono/cors";
import { HTTPException as HTTPException2 } from "hono/http-exception";
import { ZodError } from "zod";

// src/server/router.ts
import { Hono } from "hono";
import { env } from "hono/adapter";
import { HTTPException } from "hono/http-exception";
import { logger as logger2, ServerSocket } from "jstack-shared";
import { z } from "zod";

// src/server/io.ts
import { logger } from "jstack-shared";
var IO = class {
  targetRoom = null;
  redisUrl;
  redisToken;
  constructor(redisUrl, redisToken) {
    this.redisUrl = redisUrl;
    this.redisToken = redisToken;
  }
  /**
   * Sends to all connected clients (broadcast)
   */
  async emit(event, data) {
    if (this.targetRoom) {
      await fetch(`${this.redisUrl}/publish/${this.targetRoom}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.redisToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify([event, data])
      });
    }
    logger.success(`IO emitted to room "${this.targetRoom}":`, [event, data]);
    this.targetRoom = null;
  }
  /**
   * Sends to all in a room
   */
  to(room) {
    this.targetRoom = room;
    return this;
  }
};

// node_modules/superjson/dist/double-indexed-kv.js
var DoubleIndexedKV = class {
  constructor() {
    this.keyToValue = /* @__PURE__ */ new Map();
    this.valueToKey = /* @__PURE__ */ new Map();
  }
  set(key, value) {
    this.keyToValue.set(key, value);
    this.valueToKey.set(value, key);
  }
  getByKey(key) {
    return this.keyToValue.get(key);
  }
  getByValue(value) {
    return this.valueToKey.get(value);
  }
  clear() {
    this.keyToValue.clear();
    this.valueToKey.clear();
  }
};

// node_modules/superjson/dist/registry.js
var Registry = class {
  constructor(generateIdentifier) {
    this.generateIdentifier = generateIdentifier;
    this.kv = new DoubleIndexedKV();
  }
  register(value, identifier) {
    if (this.kv.getByValue(value)) {
      return;
    }
    if (!identifier) {
      identifier = this.generateIdentifier(value);
    }
    this.kv.set(identifier, value);
  }
  clear() {
    this.kv.clear();
  }
  getIdentifier(value) {
    return this.kv.getByValue(value);
  }
  getValue(identifier) {
    return this.kv.getByKey(identifier);
  }
};

// node_modules/superjson/dist/class-registry.js
var ClassRegistry = class extends Registry {
  constructor() {
    super((c) => c.name);
    this.classToAllowedProps = /* @__PURE__ */ new Map();
  }
  register(value, options) {
    if (typeof options === "object") {
      if (options.allowProps) {
        this.classToAllowedProps.set(value, options.allowProps);
      }
      super.register(value, options.identifier);
    } else {
      super.register(value, options);
    }
  }
  getAllowedProps(value) {
    return this.classToAllowedProps.get(value);
  }
};

// node_modules/superjson/dist/util.js
function valuesOfObj(record) {
  if ("values" in Object) {
    return Object.values(record);
  }
  const values = [];
  for (const key in record) {
    if (record.hasOwnProperty(key)) {
      values.push(record[key]);
    }
  }
  return values;
}
function find(record, predicate) {
  const values = valuesOfObj(record);
  if ("find" in values) {
    return values.find(predicate);
  }
  const valuesNotNever = values;
  for (let i = 0; i < valuesNotNever.length; i++) {
    const value = valuesNotNever[i];
    if (predicate(value)) {
      return value;
    }
  }
  return void 0;
}
function forEach(record, run) {
  Object.entries(record).forEach(([key, value]) => run(value, key));
}
function includes(arr, value) {
  return arr.indexOf(value) !== -1;
}
function findArr(record, predicate) {
  for (let i = 0; i < record.length; i++) {
    const value = record[i];
    if (predicate(value)) {
      return value;
    }
  }
  return void 0;
}

// node_modules/superjson/dist/custom-transformer-registry.js
var CustomTransformerRegistry = class {
  constructor() {
    this.transfomers = {};
  }
  register(transformer) {
    this.transfomers[transformer.name] = transformer;
  }
  findApplicable(v) {
    return find(this.transfomers, (transformer) => transformer.isApplicable(v));
  }
  findByName(name) {
    return this.transfomers[name];
  }
};

// node_modules/superjson/dist/is.js
var getType = (payload) => Object.prototype.toString.call(payload).slice(8, -1);
var isUndefined = (payload) => typeof payload === "undefined";
var isNull = (payload) => payload === null;
var isPlainObject = (payload) => {
  if (typeof payload !== "object" || payload === null)
    return false;
  if (payload === Object.prototype)
    return false;
  if (Object.getPrototypeOf(payload) === null)
    return true;
  return Object.getPrototypeOf(payload) === Object.prototype;
};
var isEmptyObject = (payload) => isPlainObject(payload) && Object.keys(payload).length === 0;
var isArray = (payload) => Array.isArray(payload);
var isString = (payload) => typeof payload === "string";
var isNumber = (payload) => typeof payload === "number" && !isNaN(payload);
var isBoolean = (payload) => typeof payload === "boolean";
var isRegExp = (payload) => payload instanceof RegExp;
var isMap = (payload) => payload instanceof Map;
var isSet = (payload) => payload instanceof Set;
var isSymbol = (payload) => getType(payload) === "Symbol";
var isDate = (payload) => payload instanceof Date && !isNaN(payload.valueOf());
var isError = (payload) => payload instanceof Error;
var isNaNValue = (payload) => typeof payload === "number" && isNaN(payload);
var isPrimitive = (payload) => isBoolean(payload) || isNull(payload) || isUndefined(payload) || isNumber(payload) || isString(payload) || isSymbol(payload);
var isBigint = (payload) => typeof payload === "bigint";
var isInfinite = (payload) => payload === Infinity || payload === -Infinity;
var isTypedArray = (payload) => ArrayBuffer.isView(payload) && !(payload instanceof DataView);
var isURL = (payload) => payload instanceof URL;

// node_modules/superjson/dist/pathstringifier.js
var escapeKey = (key) => key.replace(/\./g, "\\.");
var stringifyPath = (path) => path.map(String).map(escapeKey).join(".");
var parsePath = (string) => {
  const result = [];
  let segment = "";
  for (let i = 0; i < string.length; i++) {
    let char = string.charAt(i);
    const isEscapedDot = char === "\\" && string.charAt(i + 1) === ".";
    if (isEscapedDot) {
      segment += ".";
      i++;
      continue;
    }
    const isEndOfSegment = char === ".";
    if (isEndOfSegment) {
      result.push(segment);
      segment = "";
      continue;
    }
    segment += char;
  }
  const lastSegment = segment;
  result.push(lastSegment);
  return result;
};

// node_modules/superjson/dist/transformer.js
function simpleTransformation(isApplicable, annotation, transform, untransform) {
  return {
    isApplicable,
    annotation,
    transform,
    untransform
  };
}
var simpleRules = [
  simpleTransformation(isUndefined, "undefined", () => null, () => void 0),
  simpleTransformation(isBigint, "bigint", (v) => v.toString(), (v) => {
    if (typeof BigInt !== "undefined") {
      return BigInt(v);
    }
    console.error("Please add a BigInt polyfill.");
    return v;
  }),
  simpleTransformation(isDate, "Date", (v) => v.toISOString(), (v) => new Date(v)),
  simpleTransformation(isError, "Error", (v, superJson) => {
    const baseError = {
      name: v.name,
      message: v.message
    };
    superJson.allowedErrorProps.forEach((prop) => {
      baseError[prop] = v[prop];
    });
    return baseError;
  }, (v, superJson) => {
    const e = new Error(v.message);
    e.name = v.name;
    e.stack = v.stack;
    superJson.allowedErrorProps.forEach((prop) => {
      e[prop] = v[prop];
    });
    return e;
  }),
  simpleTransformation(isRegExp, "regexp", (v) => "" + v, (regex) => {
    const body = regex.slice(1, regex.lastIndexOf("/"));
    const flags = regex.slice(regex.lastIndexOf("/") + 1);
    return new RegExp(body, flags);
  }),
  simpleTransformation(
    isSet,
    "set",
    // (sets only exist in es6+)
    // eslint-disable-next-line es5/no-es6-methods
    (v) => [...v.values()],
    (v) => new Set(v)
  ),
  simpleTransformation(isMap, "map", (v) => [...v.entries()], (v) => new Map(v)),
  simpleTransformation((v) => isNaNValue(v) || isInfinite(v), "number", (v) => {
    if (isNaNValue(v)) {
      return "NaN";
    }
    if (v > 0) {
      return "Infinity";
    } else {
      return "-Infinity";
    }
  }, Number),
  simpleTransformation((v) => v === 0 && 1 / v === -Infinity, "number", () => {
    return "-0";
  }, Number),
  simpleTransformation(isURL, "URL", (v) => v.toString(), (v) => new URL(v))
];
function compositeTransformation(isApplicable, annotation, transform, untransform) {
  return {
    isApplicable,
    annotation,
    transform,
    untransform
  };
}
var symbolRule = compositeTransformation((s, superJson) => {
  if (isSymbol(s)) {
    const isRegistered = !!superJson.symbolRegistry.getIdentifier(s);
    return isRegistered;
  }
  return false;
}, (s, superJson) => {
  const identifier = superJson.symbolRegistry.getIdentifier(s);
  return ["symbol", identifier];
}, (v) => v.description, (_, a, superJson) => {
  const value = superJson.symbolRegistry.getValue(a[1]);
  if (!value) {
    throw new Error("Trying to deserialize unknown symbol");
  }
  return value;
});
var constructorToName = [
  Int8Array,
  Uint8Array,
  Int16Array,
  Uint16Array,
  Int32Array,
  Uint32Array,
  Float32Array,
  Float64Array,
  Uint8ClampedArray
].reduce((obj, ctor) => {
  obj[ctor.name] = ctor;
  return obj;
}, {});
var typedArrayRule = compositeTransformation(isTypedArray, (v) => ["typed-array", v.constructor.name], (v) => [...v], (v, a) => {
  const ctor = constructorToName[a[1]];
  if (!ctor) {
    throw new Error("Trying to deserialize unknown typed array");
  }
  return new ctor(v);
});
function isInstanceOfRegisteredClass(potentialClass, superJson) {
  if (potentialClass?.constructor) {
    const isRegistered = !!superJson.classRegistry.getIdentifier(potentialClass.constructor);
    return isRegistered;
  }
  return false;
}
var classRule = compositeTransformation(isInstanceOfRegisteredClass, (clazz, superJson) => {
  const identifier = superJson.classRegistry.getIdentifier(clazz.constructor);
  return ["class", identifier];
}, (clazz, superJson) => {
  const allowedProps = superJson.classRegistry.getAllowedProps(clazz.constructor);
  if (!allowedProps) {
    return { ...clazz };
  }
  const result = {};
  allowedProps.forEach((prop) => {
    result[prop] = clazz[prop];
  });
  return result;
}, (v, a, superJson) => {
  const clazz = superJson.classRegistry.getValue(a[1]);
  if (!clazz) {
    throw new Error(`Trying to deserialize unknown class '${a[1]}' - check https://github.com/blitz-js/superjson/issues/116#issuecomment-773996564`);
  }
  return Object.assign(Object.create(clazz.prototype), v);
});
var customRule = compositeTransformation((value, superJson) => {
  return !!superJson.customTransformerRegistry.findApplicable(value);
}, (value, superJson) => {
  const transformer = superJson.customTransformerRegistry.findApplicable(value);
  return ["custom", transformer.name];
}, (value, superJson) => {
  const transformer = superJson.customTransformerRegistry.findApplicable(value);
  return transformer.serialize(value);
}, (v, a, superJson) => {
  const transformer = superJson.customTransformerRegistry.findByName(a[1]);
  if (!transformer) {
    throw new Error("Trying to deserialize unknown custom value");
  }
  return transformer.deserialize(v);
});
var compositeRules = [classRule, symbolRule, customRule, typedArrayRule];
var transformValue = (value, superJson) => {
  const applicableCompositeRule = findArr(compositeRules, (rule) => rule.isApplicable(value, superJson));
  if (applicableCompositeRule) {
    return {
      value: applicableCompositeRule.transform(value, superJson),
      type: applicableCompositeRule.annotation(value, superJson)
    };
  }
  const applicableSimpleRule = findArr(simpleRules, (rule) => rule.isApplicable(value, superJson));
  if (applicableSimpleRule) {
    return {
      value: applicableSimpleRule.transform(value, superJson),
      type: applicableSimpleRule.annotation
    };
  }
  return void 0;
};
var simpleRulesByAnnotation = {};
simpleRules.forEach((rule) => {
  simpleRulesByAnnotation[rule.annotation] = rule;
});
var untransformValue = (json, type, superJson) => {
  if (isArray(type)) {
    switch (type[0]) {
      case "symbol":
        return symbolRule.untransform(json, type, superJson);
      case "class":
        return classRule.untransform(json, type, superJson);
      case "custom":
        return customRule.untransform(json, type, superJson);
      case "typed-array":
        return typedArrayRule.untransform(json, type, superJson);
      default:
        throw new Error("Unknown transformation: " + type);
    }
  } else {
    const transformation = simpleRulesByAnnotation[type];
    if (!transformation) {
      throw new Error("Unknown transformation: " + type);
    }
    return transformation.untransform(json, superJson);
  }
};

// node_modules/superjson/dist/accessDeep.js
var getNthKey = (value, n) => {
  if (n > value.size)
    throw new Error("index out of bounds");
  const keys = value.keys();
  while (n > 0) {
    keys.next();
    n--;
  }
  return keys.next().value;
};
function validatePath(path) {
  if (includes(path, "__proto__")) {
    throw new Error("__proto__ is not allowed as a property");
  }
  if (includes(path, "prototype")) {
    throw new Error("prototype is not allowed as a property");
  }
  if (includes(path, "constructor")) {
    throw new Error("constructor is not allowed as a property");
  }
}
var getDeep = (object, path) => {
  validatePath(path);
  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (isSet(object)) {
      object = getNthKey(object, +key);
    } else if (isMap(object)) {
      const row = +key;
      const type = +path[++i] === 0 ? "key" : "value";
      const keyOfRow = getNthKey(object, row);
      switch (type) {
        case "key":
          object = keyOfRow;
          break;
        case "value":
          object = object.get(keyOfRow);
          break;
      }
    } else {
      object = object[key];
    }
  }
  return object;
};
var setDeep = (object, path, mapper) => {
  validatePath(path);
  if (path.length === 0) {
    return mapper(object);
  }
  let parent = object;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (isArray(parent)) {
      const index = +key;
      parent = parent[index];
    } else if (isPlainObject(parent)) {
      parent = parent[key];
    } else if (isSet(parent)) {
      const row = +key;
      parent = getNthKey(parent, row);
    } else if (isMap(parent)) {
      const isEnd = i === path.length - 2;
      if (isEnd) {
        break;
      }
      const row = +key;
      const type = +path[++i] === 0 ? "key" : "value";
      const keyOfRow = getNthKey(parent, row);
      switch (type) {
        case "key":
          parent = keyOfRow;
          break;
        case "value":
          parent = parent.get(keyOfRow);
          break;
      }
    }
  }
  const lastKey = path[path.length - 1];
  if (isArray(parent)) {
    parent[+lastKey] = mapper(parent[+lastKey]);
  } else if (isPlainObject(parent)) {
    parent[lastKey] = mapper(parent[lastKey]);
  }
  if (isSet(parent)) {
    const oldValue = getNthKey(parent, +lastKey);
    const newValue = mapper(oldValue);
    if (oldValue !== newValue) {
      parent.delete(oldValue);
      parent.add(newValue);
    }
  }
  if (isMap(parent)) {
    const row = +path[path.length - 2];
    const keyToRow = getNthKey(parent, row);
    const type = +lastKey === 0 ? "key" : "value";
    switch (type) {
      case "key": {
        const newKey = mapper(keyToRow);
        parent.set(newKey, parent.get(keyToRow));
        if (newKey !== keyToRow) {
          parent.delete(keyToRow);
        }
        break;
      }
      case "value": {
        parent.set(keyToRow, mapper(parent.get(keyToRow)));
        break;
      }
    }
  }
  return object;
};

// node_modules/superjson/dist/plainer.js
function traverse(tree, walker2, origin = []) {
  if (!tree) {
    return;
  }
  if (!isArray(tree)) {
    forEach(tree, (subtree, key) => traverse(subtree, walker2, [...origin, ...parsePath(key)]));
    return;
  }
  const [nodeValue, children] = tree;
  if (children) {
    forEach(children, (child, key) => {
      traverse(child, walker2, [...origin, ...parsePath(key)]);
    });
  }
  walker2(nodeValue, origin);
}
function applyValueAnnotations(plain, annotations, superJson) {
  traverse(annotations, (type, path) => {
    plain = setDeep(plain, path, (v) => untransformValue(v, type, superJson));
  });
  return plain;
}
function applyReferentialEqualityAnnotations(plain, annotations) {
  function apply(identicalPaths, path) {
    const object = getDeep(plain, parsePath(path));
    identicalPaths.map(parsePath).forEach((identicalObjectPath) => {
      plain = setDeep(plain, identicalObjectPath, () => object);
    });
  }
  if (isArray(annotations)) {
    const [root, other] = annotations;
    root.forEach((identicalPath) => {
      plain = setDeep(plain, parsePath(identicalPath), () => plain);
    });
    if (other) {
      forEach(other, apply);
    }
  } else {
    forEach(annotations, apply);
  }
  return plain;
}
var isDeep = (object, superJson) => isPlainObject(object) || isArray(object) || isMap(object) || isSet(object) || isInstanceOfRegisteredClass(object, superJson);
function addIdentity(object, path, identities) {
  const existingSet = identities.get(object);
  if (existingSet) {
    existingSet.push(path);
  } else {
    identities.set(object, [path]);
  }
}
function generateReferentialEqualityAnnotations(identitites, dedupe) {
  const result = {};
  let rootEqualityPaths = void 0;
  identitites.forEach((paths) => {
    if (paths.length <= 1) {
      return;
    }
    if (!dedupe) {
      paths = paths.map((path) => path.map(String)).sort((a, b) => a.length - b.length);
    }
    const [representativePath, ...identicalPaths] = paths;
    if (representativePath.length === 0) {
      rootEqualityPaths = identicalPaths.map(stringifyPath);
    } else {
      result[stringifyPath(representativePath)] = identicalPaths.map(stringifyPath);
    }
  });
  if (rootEqualityPaths) {
    if (isEmptyObject(result)) {
      return [rootEqualityPaths];
    } else {
      return [rootEqualityPaths, result];
    }
  } else {
    return isEmptyObject(result) ? void 0 : result;
  }
}
var walker = (object, identities, superJson, dedupe, path = [], objectsInThisPath = [], seenObjects = /* @__PURE__ */ new Map()) => {
  const primitive = isPrimitive(object);
  if (!primitive) {
    addIdentity(object, path, identities);
    const seen = seenObjects.get(object);
    if (seen) {
      return dedupe ? {
        transformedValue: null
      } : seen;
    }
  }
  if (!isDeep(object, superJson)) {
    const transformed2 = transformValue(object, superJson);
    const result2 = transformed2 ? {
      transformedValue: transformed2.value,
      annotations: [transformed2.type]
    } : {
      transformedValue: object
    };
    if (!primitive) {
      seenObjects.set(object, result2);
    }
    return result2;
  }
  if (includes(objectsInThisPath, object)) {
    return {
      transformedValue: null
    };
  }
  const transformationResult = transformValue(object, superJson);
  const transformed = transformationResult?.value ?? object;
  const transformedValue = isArray(transformed) ? [] : {};
  const innerAnnotations = {};
  forEach(transformed, (value, index) => {
    if (index === "__proto__" || index === "constructor" || index === "prototype") {
      throw new Error(`Detected property ${index}. This is a prototype pollution risk, please remove it from your object.`);
    }
    const recursiveResult = walker(value, identities, superJson, dedupe, [...path, index], [...objectsInThisPath, object], seenObjects);
    transformedValue[index] = recursiveResult.transformedValue;
    if (isArray(recursiveResult.annotations)) {
      innerAnnotations[index] = recursiveResult.annotations;
    } else if (isPlainObject(recursiveResult.annotations)) {
      forEach(recursiveResult.annotations, (tree, key) => {
        innerAnnotations[escapeKey(index) + "." + key] = tree;
      });
    }
  });
  const result = isEmptyObject(innerAnnotations) ? {
    transformedValue,
    annotations: !!transformationResult ? [transformationResult.type] : void 0
  } : {
    transformedValue,
    annotations: !!transformationResult ? [transformationResult.type, innerAnnotations] : innerAnnotations
  };
  if (!primitive) {
    seenObjects.set(object, result);
  }
  return result;
};

// node_modules/is-what/dist/index.js
function getType2(payload) {
  return Object.prototype.toString.call(payload).slice(8, -1);
}
function isArray2(payload) {
  return getType2(payload) === "Array";
}
function isPlainObject2(payload) {
  if (getType2(payload) !== "Object")
    return false;
  const prototype = Object.getPrototypeOf(payload);
  return !!prototype && prototype.constructor === Object && prototype === Object.prototype;
}
function isNull2(payload) {
  return getType2(payload) === "Null";
}
function isOneOf(a, b, c, d, e) {
  return (value) => a(value) || b(value) || !!c && c(value) || !!d && d(value) || !!e && e(value);
}
function isUndefined2(payload) {
  return getType2(payload) === "Undefined";
}
var isNullOrUndefined = isOneOf(isNull2, isUndefined2);

// node_modules/copy-anything/dist/index.js
function assignProp(carry, key, newVal, originalObject, includeNonenumerable) {
  const propType = {}.propertyIsEnumerable.call(originalObject, key) ? "enumerable" : "nonenumerable";
  if (propType === "enumerable")
    carry[key] = newVal;
  if (includeNonenumerable && propType === "nonenumerable") {
    Object.defineProperty(carry, key, {
      value: newVal,
      enumerable: false,
      writable: true,
      configurable: true
    });
  }
}
function copy(target, options = {}) {
  if (isArray2(target)) {
    return target.map((item) => copy(item, options));
  }
  if (!isPlainObject2(target)) {
    return target;
  }
  const props = Object.getOwnPropertyNames(target);
  const symbols = Object.getOwnPropertySymbols(target);
  return [...props, ...symbols].reduce((carry, key) => {
    if (isArray2(options.props) && !options.props.includes(key)) {
      return carry;
    }
    const val = target[key];
    const newVal = copy(val, options);
    assignProp(carry, key, newVal, target, options.nonenumerable);
    return carry;
  }, {});
}

// node_modules/superjson/dist/index.js
var SuperJSON = class {
  /**
   * @param dedupeReferentialEqualities  If true, SuperJSON will make sure only one instance of referentially equal objects are serialized and the rest are replaced with `null`.
   */
  constructor({ dedupe = false } = {}) {
    this.classRegistry = new ClassRegistry();
    this.symbolRegistry = new Registry((s) => s.description ?? "");
    this.customTransformerRegistry = new CustomTransformerRegistry();
    this.allowedErrorProps = [];
    this.dedupe = dedupe;
  }
  serialize(object) {
    const identities = /* @__PURE__ */ new Map();
    const output = walker(object, identities, this, this.dedupe);
    const res = {
      json: output.transformedValue
    };
    if (output.annotations) {
      res.meta = {
        ...res.meta,
        values: output.annotations
      };
    }
    const equalityAnnotations = generateReferentialEqualityAnnotations(identities, this.dedupe);
    if (equalityAnnotations) {
      res.meta = {
        ...res.meta,
        referentialEqualities: equalityAnnotations
      };
    }
    return res;
  }
  deserialize(payload) {
    const { json, meta } = payload;
    let result = copy(json);
    if (meta?.values) {
      result = applyValueAnnotations(result, meta.values, this);
    }
    if (meta?.referentialEqualities) {
      result = applyReferentialEqualityAnnotations(result, meta.referentialEqualities);
    }
    return result;
  }
  stringify(object) {
    return JSON.stringify(this.serialize(object));
  }
  parse(string) {
    return this.deserialize(JSON.parse(string));
  }
  registerClass(v, options) {
    this.classRegistry.register(v, options);
  }
  registerSymbol(v, identifier) {
    this.symbolRegistry.register(v, identifier);
  }
  registerCustom(transformer, name) {
    this.customTransformerRegistry.register({
      name,
      ...transformer
    });
  }
  allowErrorProps(...props) {
    this.allowedErrorProps.push(...props);
  }
};
SuperJSON.defaultInstance = new SuperJSON();
SuperJSON.serialize = SuperJSON.defaultInstance.serialize.bind(SuperJSON.defaultInstance);
SuperJSON.deserialize = SuperJSON.defaultInstance.deserialize.bind(SuperJSON.defaultInstance);
SuperJSON.stringify = SuperJSON.defaultInstance.stringify.bind(SuperJSON.defaultInstance);
SuperJSON.parse = SuperJSON.defaultInstance.parse.bind(SuperJSON.defaultInstance);
SuperJSON.registerClass = SuperJSON.defaultInstance.registerClass.bind(SuperJSON.defaultInstance);
SuperJSON.registerSymbol = SuperJSON.defaultInstance.registerSymbol.bind(SuperJSON.defaultInstance);
SuperJSON.registerCustom = SuperJSON.defaultInstance.registerCustom.bind(SuperJSON.defaultInstance);
SuperJSON.allowErrorProps = SuperJSON.defaultInstance.allowErrorProps.bind(SuperJSON.defaultInstance);
var serialize = SuperJSON.serialize;
var deserialize = SuperJSON.deserialize;
var stringify = SuperJSON.stringify;
var parse = SuperJSON.parse;
var registerClass = SuperJSON.registerClass;
var registerCustom = SuperJSON.registerCustom;
var registerSymbol = SuperJSON.registerSymbol;
var allowErrorProps = SuperJSON.allowErrorProps;

// src/server/middleware/utils.ts
var parseSuperJSON = (value) => {
  try {
    return SuperJSON.parse(value);
  } catch {
    return value;
  }
};

// src/server/middleware/index.ts
var queryParsingMiddleware = async function queryParsingMiddleware2(c, next) {
  const rawQuery = c.req.query();
  const parsedQuery = {};
  for (const [key, value] of Object.entries(rawQuery)) {
    parsedQuery[key] = parseSuperJSON(value);
  }
  c.set("__parsed_query", parsedQuery);
  await next();
};
var bodyParsingMiddleware = async function bodyParsingMiddleware2(c, next) {
  const rawBody = await c.req.json();
  const parsedBody = {};
  for (const [key, value] of Object.entries(rawBody)) {
    parsedBody[key] = parseSuperJSON(value);
  }
  c.set("__parsed_body", parsedBody);
  await next();
};

// src/server/router.ts
var Router = class extends Hono {
  _metadata;
  _errorHandler = void 0;
  config(config) {
    if (config) {
      this._metadata.config = config;
    }
    return this;
  }
  // Used in Hono adapters
  // Strips types to prevent version-mismatch induced infinite recursion warning
  get handler() {
    return this;
  }
  constructor(procedures = {}) {
    super();
    this._metadata = {
      subRouters: {},
      config: {},
      procedures: {},
      registeredPaths: []
    };
    this.onError = (handler) => {
      this._errorHandler = handler;
      return this;
    };
    this.setupRoutes(procedures);
  }
  registerSubrouterMiddleware() {
    this.use(async (c, next) => {
      const [basePath, routerName] = c.req.path.split("/").filter(Boolean).slice(0, 2);
      const key = `/${basePath}/${routerName}`;
      const subRouter = await this._metadata.subRouters[key];
      if (subRouter) {
        const rewrittenPath = "/" + c.req.path.split("/").slice(3).join("/");
        const newUrl = new URL(c.req.url);
        newUrl.pathname = rewrittenPath;
        const newRequest = new Request(newUrl, c.req.raw);
        const response = await subRouter.fetch(newRequest, c.env);
        return response;
      }
      return next();
    });
  }
  setupRoutes(procedures) {
    Object.entries(procedures).forEach(([key, value]) => {
      if (this.isOperationType(value)) {
        this.registerOperation(key, value);
      } else if (typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (this.isOperationType(subValue)) {
            this.registerOperation(`${key}/${subKey}`, subValue);
          }
        });
      }
    });
  }
  isOperationType(value) {
    return value && typeof value === "object" && "type" in value && (value.type === "get" || value.type === "post" || value.type === "ws");
  }
  registerOperation(path, operation) {
    const routePath = `/${path}`;
    if (!this._metadata.procedures[path]) {
      this._metadata.procedures[path] = {
        type: operation.type
      };
    }
    const operationMiddlewares = operation.middlewares.map((middleware) => {
      const middlewareHandler = async (c, next) => {
        const typedC = c;
        let middlewareOutput = typedC.get("__middleware_output") ?? {};
        const nextWrapper = async (args) => {
          Object.assign(middlewareOutput, args);
          return middlewareOutput;
        };
        const res = await middleware({
          ctx: middlewareOutput,
          next: nextWrapper,
          c
        });
        if (res) {
          Object.assign(middlewareOutput, res);
        }
        typedC.set("__middleware_output", middlewareOutput);
        await next();
      };
      return middlewareHandler;
    });
    if (operation.type === "get") {
      if (operation.schema) {
        this.get(
          routePath,
          queryParsingMiddleware,
          ...operationMiddlewares,
          async (c) => {
            const typedC = c;
            const ctx = typedC.get("__middleware_output") || {};
            const parsedQuery = typedC.get("__parsed_query");
            const queryInput = Object.keys(parsedQuery || {}).length === 0 ? void 0 : parsedQuery;
            const input = operation.schema?.parse(queryInput);
            const result = await operation.handler({
              c,
              ctx,
              input
            });
            return result === void 0 ? c.json(void 0) : result;
          }
        );
      } else {
        this.get(routePath, ...operationMiddlewares, async (c) => {
          const typedC = c;
          const ctx = typedC.get("__middleware_output") || {};
          const result = await operation.handler({
            c,
            ctx,
            input: void 0
          });
          return result === void 0 ? c.json(void 0) : result;
        });
      }
    } else if (operation.type === "post") {
      if (operation.schema) {
        this.post(
          routePath,
          bodyParsingMiddleware,
          ...operationMiddlewares,
          async (c) => {
            const typedC = c;
            const ctx = typedC.get("__middleware_output") || {};
            const parsedBody = typedC.get("__parsed_body");
            const bodyInput = Object.keys(parsedBody || {}).length === 0 ? void 0 : parsedBody;
            const input = operation.schema?.parse(bodyInput);
            const result = await operation.handler({
              c,
              ctx,
              input
            });
            return result === void 0 ? c.json(void 0) : result;
          }
        );
      } else {
        this.post(routePath, ...operationMiddlewares, async (c) => {
          const typedC = c;
          const ctx = typedC.get("__middleware_output") || {};
          const result = await operation.handler({
            c,
            ctx,
            input: void 0
          });
          return result === void 0 ? c.json(void 0) : result;
        });
      }
    } else if (operation.type === "ws") {
      this.get(
        routePath,
        queryParsingMiddleware,
        ...operationMiddlewares,
        async (c) => {
          const typedC = c;
          const { UPSTASH_REDIS_REST_URL, UPSTASH_REDIS_REST_TOKEN } = env(typedC);
          if (!UPSTASH_REDIS_REST_URL || !UPSTASH_REDIS_REST_TOKEN) {
            throw new HTTPException(503, {
              message: "Missing required environment variables for WebSockets connection.\n\nReal-time WebSockets depend on a persistent connection layer to maintain communication. JStack uses Upstash Redis to achieve this.To fix this error:\n1. Log in to Upstash Redis at https://upstash.com\n2. Add UPSTASH_REDIS_REST_URL and UPSTASH_REDIS_REST_TOKEN to your environment variables\n\nComplete WebSockets guide: https://jstack.app/docs/websockets\n"
            });
          }
          const ctx = typedC.get("__middleware_output") || {};
          const { 0: client, 1: server } = new WebSocketPair();
          server.accept();
          const io = new IO(UPSTASH_REDIS_REST_URL, UPSTASH_REDIS_REST_TOKEN);
          const handler = await operation.handler({
            io,
            c,
            ctx
          });
          const socket = new ServerSocket(server, {
            redisUrl: UPSTASH_REDIS_REST_URL,
            redisToken: UPSTASH_REDIS_REST_TOKEN,
            incomingSchema: operation.incoming,
            outgoingSchema: operation.outgoing
          });
          handler.onConnect?.({ socket });
          server.onclose = async () => {
            socket.close();
            await handler.onDisconnect?.({ socket });
          };
          server.onerror = async (error) => {
            socket.close();
            await handler.onError?.({ socket, error });
          };
          const eventSchema = z.tuple([z.string(), z.unknown()]);
          server.onmessage = async (event) => {
            try {
              const rawData = z.string().parse(event.data);
              const parsedData = JSON.parse(rawData);
              const [eventName, eventData] = eventSchema.parse(parsedData);
              if (eventName === "ping") {
                server.send(JSON.stringify(["pong", null]));
                return;
              }
              socket.handleEvent(eventName, eventData);
            } catch (err) {
              logger2.error("Failed to process message:", err);
            }
          };
          return new Response(null, {
            status: 101,
            webSocket: client
          });
        }
      );
    }
  }
};

// src/server/merge-routers.ts
function mergeRouters(api, routers) {
  const mergedRouter = new Router();
  Object.assign(mergedRouter, api);
  mergedRouter._metadata = {
    subRouters: {},
    config: {},
    procedures: {},
    registeredPaths: []
  };
  for (const [key, router2] of Object.entries(routers)) {
    if (typeof router2 === "function") {
      const proxyRouter = new Router();
      proxyRouter.all("*", async (c) => {
        const actualRouter = await router2();
        mergedRouter._metadata.subRouters[`/api/${key}`] = actualRouter;
        return actualRouter.fetch(c.req.raw, c.env);
      });
      mergedRouter._metadata.subRouters[`/api/${key}`] = proxyRouter;
    } else if (router2 instanceof Router) {
      mergedRouter._metadata.subRouters[`/api/${key}`] = router2;
    }
  }
  mergedRouter.registerSubrouterMiddleware();
  return mergedRouter;
}

// src/server/procedure.ts
var Procedure = class _Procedure {
  middlewares = [];
  inputSchema;
  incomingSchema;
  outgoingSchema;
  superjsonMiddleware = async function superjsonMiddleware({ c, next }) {
    c.superjson = (data, status) => {
      const serialized = SuperJSON.stringify(data);
      return c.newResponse(serialized, status, {
        ...Object.fromEntries(c.res.headers.entries()),
        "x-is-superjson": "true"
      });
    };
    return next();
  };
  constructor(middlewares = [], inputSchema, incomingSchema, outgoingSchema) {
    this.middlewares = middlewares;
    this.inputSchema = inputSchema;
    this.incomingSchema = incomingSchema;
    this.outgoingSchema = outgoingSchema;
    if (!this.middlewares.some((mw) => mw.name === "superjsonMiddleware")) {
      this.middlewares.push(this.superjsonMiddleware);
    }
  }
  /**
   * Validates incoming WebSocket messages using a Zod schema.
   *
   * @see https://jstack.app/docs/backend/websockets
   * @param schema - A Zod schema to validate incoming WebSocket messages
   *
   * @example
   * ```ts
   * const chatValidator = z.object({
   *   message: z.object({
   *     roomId: z.string(),
   *     message: z.string(),
   *     author: z.string()
   *   })
   * })
   *
   * const chatRouter = router({
   *   chat: procedure
   *     .incoming(chatValidator)
   *     .ws(({ io }) => ({
   *       onConnect({ socket }) {
   *         socket.on("message", ({ roomId, message, author }) => {
   *           // ...
   *         })
   *       }
   *     }))
   * })
   * ```
   */
  incoming(schema) {
    return new _Procedure(
      this.middlewares,
      this.inputSchema,
      schema,
      this.outgoingSchema
    );
  }
  /**
   * Validates outgoing WebSocket messages using a Zod schema.
   *
   * @see https://jstack.app/docs/backend/websockets
   * @param schema - A Zod schema to validate outgoing WebSocket messages
   *
   * @example
   * ```ts
   * const chatValidator = z.object({
   *   message: z.object({
   *     roomId: z.string(),
   *     message: z.string(),
   *     author: z.string()
   *   })
   * })
   *
   * const chatRouter = router({
   *   chat: procedure
   *     .incoming(chatValidator)
   *     .outgoing(chatValidator)
   *     .ws(({ io }) => ({
   *       onConnect({ socket }) {
   *         socket.on("message", async (message) => {
   *           await io.to(message.roomId).emit("message", message)
   *         })
   *       }
   *     }))
   * })
   * ```
   */
  outgoing(schema) {
    return new _Procedure(
      this.middlewares,
      this.inputSchema,
      this.incomingSchema,
      schema
    );
  }
  /**
   * Validates input parameters using a Zod schema.
   * 
   * @see https://jstack.app/docs/backend/procedures#input-validation
   * @param schema - A Zod schema to validate input parameters
   * 
   * @example
   * ```ts
   * const router = j.router({
   *   hello: publicProcedure
   *     .input(z.object({ name: z.string() }))
   *     .get(({ c, input }) => {
   *       return c.text(`Hello ${input.name}!`) // input is typed as { name: string }
   *     })
   * })
   * ```
   */
  input(schema) {
    return new _Procedure(
      this.middlewares,
      schema,
      this.incomingSchema,
      this.outgoingSchema
    );
  }
  /**
   * Adds a middleware function to the procedure chain.
   * 
   * @see https://jstack.app/docs/backend/middleware
   * @param handler - A middleware function that can modify the context
   * 
   * @example
   * ```ts
   * // Create a middleware that adds user data to context
   * const withUser = j.middleware(async ({ ctx, next }) => {
   *   const user = await getUser()
   *   return await next({ user }) // Adds user to ctx
   * })
   * 
   * const router = j.router({
   *   profile: publicProcedure
   *     .use(withUser) // Apply middleware
   *     .get(({ c, ctx }) => {
   *       return c.json(ctx.user) // ctx.user is now typed
   *     })
   * })
   * ```
   */
  use(handler) {
    return new _Procedure(
      [...this.middlewares, handler],
      this.inputSchema,
      this.incomingSchema,
      this.outgoingSchema
    );
  }
  get(handler) {
    return {
      type: "get",
      schema: this.inputSchema,
      handler,
      middlewares: this.middlewares
    };
  }
  query(handler) {
    return this.get(handler);
  }
  post(handler) {
    return {
      type: "post",
      schema: this.inputSchema,
      handler,
      middlewares: this.middlewares
    };
  }
  mutation(handler) {
    return this.post(handler);
  }
  ws(handler) {
    return {
      type: "ws",
      outputFormat: "ws",
      handler,
      middlewares: this.middlewares
    };
  }
};

// src/server/j.ts
var router = (procedures = {}) => {
  return new Router(procedures);
};
function fromHono(honoMiddleware) {
  return async ({ c, next }) => {
    await honoMiddleware(c, async () => {
      const result = await next();
      return result;
    });
  };
}
var JStack = class {
  init() {
    return {
      /**
       * Type-safe router factory function that creates a new router instance.
       * 
       * @template T - Record of operation types (get/post/websockets)
       * @template E - Environment type for the router
       * @returns {Router<T, E>} A new router instance with type-safe procedure definitions
       * 
       * @example
       * const userRouter = router({
       *   getUser: publicProcedure
       *     .input(z.object({ id: z.string() }))
       *     .get(async ({ input }) => {
       *       return { id: input.id, name: "John Doe" }
       *     }),
       *   
       *   createUser: publicProcedure
       *     .input(z.object({ name: z.string() }))
       *     .post(async ({ input }) => {
       *       return { id: "123", name: input.name }
       *     })
       * })
       */
      router,
      mergeRouters,
      middleware: (middleware) => middleware,
      fromHono,
      procedure: new Procedure(),
      defaults: {
        /**
         * CORS middleware configuration with default settings for API endpoints.
         *
         * @default
         * - Allows 'x-is-superjson' and 'Content-Type' in headers
         * - Exposes 'x-is-superjson' in headers
         * - Accepts all origins
         * - Enables credentials
         */
        cors: cors({
          allowHeaders: ["x-is-superjson", "Content-Type"],
          exposeHeaders: ["x-is-superjson"],
          origin: (origin) => origin,
          credentials: true
        }),
        /**
         * Global error handler for API endpoints.
         * 
         * @example
         * // Client-side error handling
         * const { mutate } = useMutation({
         *   onError: (err: HTTPException) => {
         *     if (err.status === 401) {
         *       console.log(err.message) // Handle unauthorized
         *     }
         *   }
         * })
         */
        errorHandler: (err) => {
          console.error("[API Error]", err);
          if (err instanceof HTTPException2) {
            return err.getResponse();
          } else if (err instanceof ZodError) {
            const httpError = new HTTPException2(422, {
              message: "Validation error",
              cause: err
            });
            return httpError.getResponse();
          } else if ("status" in err && typeof err.status === "number") {
            const httpError = new HTTPException2(
              err.status,
              {
                message: err.message || "API Error",
                cause: err
              }
            );
            return httpError.getResponse();
          } else {
            const httpError = new HTTPException2(500, {
              message: "An unexpected error occurred. Check server logs for details.",
              cause: err
            });
            return httpError.getResponse();
          }
        }
      }
    };
  }
};
var jstack = new JStack();

// src/server/client.ts
import { hc } from "hono/client";
import { HTTPException as HTTPException3 } from "hono/http-exception";
import { ClientSocket } from "jstack-shared";
var createClient = (options) => {
  const {
    baseUrl = "",
    credentials = "include",
    ...opts
  } = options ?? {};
  const jfetch = async (input, init) => {
    const inputPath = input.toString().replace(baseUrl, "");
    const targetUrl = baseUrl + inputPath;
    const res = await fetch(targetUrl, {
      ...init,
      credentials,
      cache: "no-store"
    });
    if (!res.ok) {
      const message = await res.text();
      throw new HTTPException3(res.status, {
        message,
        res
      });
    }
    res.json = () => parseJsonResponse(res);
    return res;
  };
  const baseClient = hc(baseUrl, {
    ...opts,
    fetch: opts.fetch || jfetch
  });
  return createProxy(baseClient, baseUrl);
};
var parseJsonResponse = async (response) => {
  const text = await response.text();
  const isSuperjson = response.headers.get("x-is-superjson") === "true";
  try {
    return isSuperjson ? SuperJSON.parse(text) : JSON.parse(text);
  } catch (error) {
    console.error("Failed to parse response as JSON:", error);
    throw new Error("Invalid JSON response");
  }
};
function serializeWithSuperJSON(data) {
  if (typeof data !== "object" || data === null) {
    return data;
  }
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      SuperJSON.stringify(value)
    ])
  );
}
function createProxy(baseClient, baseUrl, path = []) {
  return new Proxy(baseClient, {
    get(target, prop, receiver) {
      if (typeof prop === "string") {
        const routePath = [...path, prop];
        if (prop === "$get") {
          return async (...args) => {
            const [data, options] = args;
            const serializedQuery = serializeWithSuperJSON(data);
            return target.$get({ query: serializedQuery }, options);
          };
        }
        if (prop === "$post") {
          return async (...args) => {
            const [data, options] = args;
            const serializedJson = serializeWithSuperJSON(data);
            return target.$post({ json: serializedJson }, options);
          };
        }
        if (prop === "$url") {
          return (args) => {
            const endpointPath = `/${routePath.slice(0, -1).join("/")}`;
            const normalizedPath = endpointPath.replace(baseUrl, "");
            const url = new URL(baseUrl + normalizedPath);
            if (args?.query) {
              Object.entries(args.query).forEach(([key, value]) => {
                url.searchParams.append(key, String(value));
              });
            }
            return url;
          };
        }
        if (prop === "$ws") {
          return () => {
            const endpointPath = `/${routePath.slice(0, -1).join("/")}`;
            const normalizedPath = endpointPath.replace(baseUrl, "");
            const url = new URL(baseUrl + normalizedPath);
            url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
            return new ClientSocket(url);
          };
        }
        return createProxy(target[prop], baseUrl, routePath);
      }
      return Reflect.get(target, prop, receiver);
    }
  });
}

// src/server/dynamic.ts
var dynamic = (importFn) => {
  return async () => {
    const module = await importFn();
    const routers = Object.values(module);
    if (routers.length === 0) {
      throw new Error(
        "Error dynamically loading router: Invalid router module - Expected a default or named export of a Router, but received an empty module. Did you forget to export your router?"
      );
    }
    if (routers.length > 1) {
      throw new Error(
        `Error dynamically loading router: Multiple Router exports detected in module (${Object.keys(module).join(", ")}). Please export only one Router instance per module.`
      );
    }
    const router2 = routers[0];
    if (!(router2 instanceof Router)) {
      throw new Error(
        `Error dynamically loading router: Invalid router module - Expected exported value to be a Router instance, but received ${router2 === null ? "null" : typeof router2}. Are you exporting multiple functions from this file?`
      );
    }
    return router2;
  };
};
export {
  Procedure,
  Router,
  createClient,
  dynamic,
  fromHono,
  jstack
};
